import {
    createJob, fetchJobs, getJobState, loadResult, removeJob,
} from 'BMapsRedux/jobs/access';
import { sleep } from 'BMapsUtil/js_utils';
import { trackPropertyGroupFetch } from 'BMapsModel/ScopedPropertyGroup';
import { JobStatus } from './JobDefinition';
import { FoldingJob } from './jobs/FoldingJob';
import { MaplightJob } from './jobs/MaplightJob';
import { GninaScoringJob } from './jobs/GninaScoringJob';
import { ReinventJob } from './jobs/ReinventJob';
import { GninaDockingJob } from './jobs/GninaDockingJob';
import { DrugFlowJob } from './jobs/DrugFlowJob';
import { LibraryScreeningJob } from './jobs/LibraryScreeningJob';
import { RxnEnumJob } from './jobs/RxnEnumJob';
import { MolexarJob } from './jobs/MolexarJob';
import {
    OpenFERbfeJob, OpenFEAbfeJob, TmdRbfeJob, GromacsRbfeJob,
} from './jobs/FepScoringJob';
import { StormmMdJob, GromacsMdJob, OpenmmMdJob } from './jobs/MdSimulationJob';

export const jobClasses = [
    FoldingJob,
    MaplightJob,
    GninaScoringJob,
    ReinventJob,
    GninaDockingJob,
    DrugFlowJob,
    LibraryScreeningJob,
    RxnEnumJob,
    MolexarJob,
    OpenFERbfeJob,
    OpenFEAbfeJob,
    TmdRbfeJob,
    GromacsRbfeJob,
    StormmMdJob,
    GromacsMdJob,
    OpenmmMdJob,
];

export const jobClassList = Object.fromEntries(
    jobClasses.map((JobClass) => [JobClass.jobTypeId, JobClass])
);

/**
 * Friendly enum of job-type ids (`JobTypes.DRUGFLOW === 'drugflow'`),
 * derived from `jobClasses` —
 * `jobClasses` (each with its own literal `jobTypeId`) is the single
 * source of truth; this is a read-only convenience view over it for code
 * that wants to reference a job type by name without also needing its
 * `JobDefinition` subclass.
 * @type {{
 *   FOLDING: 'folding',
 *   MAPLIGHT: 'maplight',
 *   GNINA_SCORING: 'gnina_scoring',
 *   REINVENT: 'reinvent',
 *   GNINA_DOCKING: 'gnina_docking',
 *   DRUGFLOW: 'drugflow',
 *   LIBRARY_SCREEN: 'library_screen',
 *   RXN_ENUM: 'rxn_enum',
 *   MOLEXAR: 'molexar',
 *   OPENFE_RBFE: 'openfe_rbfe',
 *   OPENFE_ABFE: 'openfe_abfe',
 *   TMD_RBFE: 'tmd_rbfe',
 *   GROMACS_RBFE: 'gromacs_rbfe',
 *   STORMM_MD: 'stormm_md',
 *   GROMACS_MD: 'gromacs_md',
 *   OPENMM_MD: 'openmm_md',
 * }}
 */
export const JobTypes = Object.fromEntries(
    jobClasses.map((JobClass) => [JobClass.jobTypeId.toUpperCase(), JobClass.jobTypeId])
);

export function lookupJobDefinition(jobTypeId) {
    return jobClassList[jobTypeId];
}

export function getJobsPropertyMetadata() {
    return jobClasses
        .map((JobClass) => JobClass.getScopedPropertyMetadata())
        .filter((metadata) => metadata && Object.keys(metadata).length > 0);
}

/**
 * @param {Array<import('BMapsModel').Compound|import('BMapsModel').MapCase>} items
 * @param {string} scopeName
 * @param {{ jobtype: string, data: object, stash: object }} jobRequestData
 * @param {{
 *     trigger: function?, getValues: function?, waitSleepMs: number, checkResultsSeconds: number,
 *     refreshItems: function?
 * }} options
 * @returns {Promise<{ error: string, itemValueMap: Map }>}
 */
export async function fetchPropertyGroupWithJobman(
    items, scopeName, jobRequestData, {
        trigger, getValues, refreshItems, waitSleepMs=100, checkResultsSeconds=10,
    }={}
) {
    async function wrapper() {
        const { jobtype, data, stash } = jobRequestData;
        const { results, error } = await createJob(jobtype, data, stash);
        if (error) {
            return { error };
        }
        await waitForJobToComplete(results.jobid, waitSleepMs, checkResultsSeconds);
        // fetch results
        const integrateResultsOptions = { items };
        const result = await loadResult(results.jobid, integrateResultsOptions);
        const removed = await removeJob(results.jobid);
        if (!removed.results?.success) {
            console.warn(`Failed to delete ${results.jobid}. Error: ${removed.error}`);
        }
        if (result.error) {
            return { error: result.error };
        }

        const itemValueMap = new Map();
        if (getValues) {
            for (const item of items) {
                itemValueMap.set(item, getValues(result.results, item));
            }
        } // otherwise, job integration filled in the values

        return { itemValueMap };
    }

    return trackPropertyGroupFetch(items, scopeName, wrapper, trigger, refreshItems);
}

export async function waitForJobToComplete(
    jobid, waitSleepMs=100, checkResultsSeconds=10
) {
    setTimeout(() => {
        fetchJobs();
    }, checkResultsSeconds * 1000);
    let jobComplete = false;
    while (!jobComplete) {
        await sleep(waitSleepMs);
        const job = getJobState().jobs.find((j) => j.jobid === jobid);
        if (job?.state === JobStatus.SUCCEEDED || job?.state === JobStatus.FAILED) {
            jobComplete = true;
        }
    }
}
